import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import SidebarLayout from '../../components/layout/SidebarLayout';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { ArrowLeft, Plus, Upload, Search, Loader2, FileText, Trash2, Eye, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { Checkbox } from '../../components/ui/checkbox';
import { TimeSelect } from '../../components/ui/TimeSelect';
import { useConfirm } from '../../components/common/ConfirmDialog';

/* ─── CrmSearchField ── defined at MODULE LEVEL so React never remounts it ───
   Moving it inside a component causes focus-loss on every keystroke because
   React treats the inline-defined component as a new type on each render.     */
const CrmSearchField = ({
  label, value, search, onSearch, results, onSelect, onClear,
  createType, placeholder, fieldKey, allItems, required = true,
  focusedField, setFocusedField,
  quickCreate, setQuickCreate,
  newName, setNewName, newLast, setNewLast,
  onQuickCreate, saving,
}) => {
  const displayResults = focusedField === fieldKey
    ? (search.length > 0 ? results : (allItems || results).slice(0, 8))
    : [];

  return (
    <div className="space-y-2">
      <Label>{label}{required ? ' *' : ''}</Label>
      {value ? (
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">{value}</Badge>
          <Button variant="ghost" size="sm" className="h-6 px-1 text-xs" onClick={onClear}>Change</Button>
        </div>
      ) : quickCreate === createType ? (
        <div className="space-y-2 p-2 border rounded">
          <div className="flex gap-2">
            {createType === 'practice' ? (
              <Input placeholder="Practice Name *" value={newName} onChange={e => setNewName(e.target.value)} className="text-sm" />
            ) : (
              <>
                <Input placeholder="First Name *" value={newName} onChange={e => setNewName(e.target.value)} className="text-sm" />
                <Input placeholder="Last Name *" value={newLast} onChange={e => setNewLast(e.target.value)} className="text-sm" />
              </>
            )}
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={onQuickCreate} disabled={saving} className="bg-primary hover:bg-primary/90 text-white text-xs">Create</Button>
            <Button size="sm" variant="outline" onClick={() => setQuickCreate(null)} className="text-xs">Cancel</Button>
          </div>
        </div>
      ) : (
        <div className="space-y-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
            <Input
              className="pl-8 h-8 text-sm"
              placeholder={placeholder}
              value={search}
              onChange={e => onSearch(e.target.value)}
              onFocus={() => setFocusedField(fieldKey)}
              onBlur={() => setTimeout(() => setFocusedField(null), 200)}
            />
          </div>
          {displayResults.length > 0 && (
            <div className="border rounded max-h-24 overflow-y-auto">
              {displayResults.slice(0, 6).map(e => (
                <div key={e.id} className="p-1.5 text-xs hover:bg-muted/50 cursor-pointer" onClick={() => onSelect(e)}>
                  {e.system_name}
                </div>
              ))}
            </div>
          )}
          <Button variant="link" size="sm" className="h-auto p-0 text-xs"
            onClick={async () => { setQuickCreate(createType); setNewName(''); setNewLast(''); }}>
            + Create New
          </Button>
        </div>
      )}
    </div>
  );
};

const VettingProceduresPage = () => {
  const { isViewOnly } = useAuth();
  const viewOnly = isViewOnly();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { saleId } = useParams();
  const [searchParams] = useSearchParams();
  const qc = useQueryClient();

  const [saleNameResolved, setSaleNameResolved] = useState(searchParams.get('name') || '');
  const [createOpen, setCreateOpen] = useState(false);
  const [ocrOpen, setOcrOpen] = useState(false);
  const [editProc, setEditProc] = useState(null);
  const [search, setSearch] = useState('');

  const { data: vettingData, isLoading: loading } = useQuery({
    queryKey: QK.vettingProcedures(saleId),
    queryFn: async () => {
      const [procRes, saleRes] = await Promise.all([
        api.get(`/vetting-procedures?sale_id=${saleId}`),
        api.get(`/sales/${saleId}`),
      ]);
      if (saleRes.data?.sale_name) setSaleNameResolved(saleRes.data.sale_name);
      return { procedures: procRes.data || [], sale: saleRes.data };
    },
    enabled: !!saleId,
  });
  const procedures = vettingData?.procedures ?? [];

  const invalidateVetting = async () => qc.invalidateQueries({ queryKey: QK.vettingProcedures(saleId) });

  const handleDelete = async (e, procId) => {
    e.stopPropagation();
    if (!await confirm({ title: 'Delete this procedure?', destructive: true })) return;
    try {
      await api.delete(`/vetting-procedures/${procId}`);
      qc.setQueryData(QK.vettingProcedures(saleId), prev =>
        prev ? { ...prev, procedures: prev.procedures.filter(p => p.id !== procId) } : prev
      );
      toast.success('Procedure deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const filtered = procedures.filter(p => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (p.vet_name || '').toLowerCase().includes(q) ||
           (p.vet_practice || '').toLowerCase().includes(q) ||
           (p.client_name || '').toLowerCase().includes(q) ||
           (p.procedure || '').toLowerCase().includes(q) ||
           (p.horse_name || '').toLowerCase().includes(q);
  });

  return (
    <SidebarLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/sales?tab=live')}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="font-serif text-2xl font-semibold">Vetting Procedures</h1>
              <p className="text-sm text-muted-foreground">{saleNameResolved}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 self-end sm:self-auto">
            <Button variant="outline" size="sm" onClick={() => setOcrOpen(true)}>
              <Upload className="w-4 h-4 mr-2" /> Scan Procedure
            </Button>
            {!viewOnly && (
            <Button size="sm" onClick={() => setCreateOpen(true)} className="bg-primary hover:bg-primary/90 text-white">
              <Plus className="w-4 h-4 mr-2" /> New Procedure
            </Button>
            )}
          </div>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">All Procedures ({filtered.length})</CardTitle>
              <div className="relative max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-8 text-sm" />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No vetting procedures recorded yet.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Hip #</TableHead>
                    <TableHead>Horse</TableHead>
                    <TableHead>Vet</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Date / Time</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(p => (
                    <TableRow key={p.id}>
                      <TableCell className="text-sm font-medium">#{p.hip_number || 'TBD'}</TableCell>
                      <TableCell className="text-sm">{p.horse_name || '-'}</TableCell>
                      <TableCell className="text-sm">
                        <span className="font-semibold">{p.vet_name || '-'}</span>
                        {p.vet_practice && <span className="block text-muted-foreground text-xs">{p.vet_practice}</span>}
                      </TableCell>
                      <TableCell className="text-sm">{p.client_name || '-'}</TableCell>
                      <TableCell className="text-sm">{p.procedure_date_time ? new Date(p.procedure_date_time).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '-'}</TableCell>
                      <TableCell className="text-sm">
                        <span className="font-medium">{p.procedure?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || '-'}</span>
                        {p.viewed_vet_report && <span title="Viewed Vet Report"><FileText className="w-3.5 h-3.5 text-blue-500 inline ml-1" /></span>}
                        {p.notes && <span className="block text-muted-foreground text-xs">{p.notes}</span>}
                      </TableCell>
                      <TableCell>
                        {!viewOnly && (
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground hover:text-primary" onClick={async (e) => { e.stopPropagation(); setEditProc(p); }} title="Edit">
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground hover:text-red-600" onClick={(e) => handleDelete(e, p.id)} title="Delete">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <CreateProcedureDialog open={createOpen} onOpenChange={setCreateOpen} saleId={saleId} onCreated={() => { setCreateOpen(false); invalidateVetting(); }} />
      <EditProcedureDialog procedure={editProc} onClose={() => setEditProc(null)} onSaved={() => { setEditProc(null); invalidateVetting(); }} />
      <OcrProcedureDialog open={ocrOpen} onOpenChange={setOcrOpen} saleId={saleId} onCreated={() => { setOcrOpen(false); invalidateVetting(); }} />
    </SidebarLayout>
  );
};

/* --- Create Procedure Dialog --- */
const CreateProcedureDialog = ({ open, onOpenChange, saleId, onCreated }) => {
  const [opportunities, setOpportunities] = useState([]);
  const [allContacts, setAllContacts] = useState([]);
  const [form, setForm] = useState({ sale_opportunity_id: '', vet_crm_entity_id: null, vet_name: '', vet_practice_crm_entity_id: null, vet_practice: '', client_crm_entity_id: null, client_name: '', procedures: [], date_part: '', time_part: '', viewed_vet_report: false, notes: '' });
  const [saving, setSaving] = useState(false);
  const [vetSearch, setVetSearch] = useState('');
  const [practiceSearch, setPracticeSearch] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [vetAssocPractices, setVetAssocPractices] = useState([]);
  const [quickCreate, setQuickCreate] = useState(null); // 'vet', 'practice', 'client'
  const [newName, setNewName] = useState('');
  const [newLast, setNewLast] = useState('');
  const [newType, setNewType] = useState('person');
  const [newOrgName, setNewOrgName] = useState('');

  // eslint-disable-next-line
  useEffect(() => {
    if (open) {
      setForm({ sale_opportunity_id: '', vet_crm_entity_id: null, vet_name: '', vet_practice_crm_entity_id: null, vet_practice: '', client_crm_entity_id: null, client_name: '', procedures: [], date_part: '', time_part: '', viewed_vet_report: false, notes: '' });
      setVetSearch(''); setPracticeSearch(''); setClientSearch(''); setVetAssocPractices([]); setQuickCreate(null);
      Promise.all([
        api.get(`/sale-opportunities?sale_id=${saleId}`),
        api.get('/crm-entities'),
      ]).then(([oppsRes, contactsRes]) => {
        setOpportunities(oppsRes.data || []);
        setAllContacts(contactsRes.data || []);
      }).catch(() => {});
    }
  }, [open, saleId]);

  const vets = allContacts.filter(c => c.person_industry_role === 'veterinarian' || c.person_industry_role === 'vet');
  const vetPractices = allContacts.filter(c => c.organization_industry_role === 'veterinary_care');
  const filteredVets = vetSearch.length > 0 ? vets.filter(v => v.system_name?.toLowerCase().includes(vetSearch.toLowerCase())) : vets;
  const filteredPractices = vetAssocPractices.length > 0 ? vetAssocPractices : (practiceSearch.length > 0 ? vetPractices.filter(v => v.system_name?.toLowerCase().includes(practiceSearch.toLowerCase())) : vetPractices);
  const filteredClients = clientSearch.length > 0 ? allContacts.filter(c => c.system_name?.toLowerCase().includes(clientSearch.toLowerCase())).slice(0, 10) : [];

  const selectVet = async (entity) => {
    setForm(f => ({ ...f, vet_crm_entity_id: entity.id, vet_name: entity.system_name }));
    setVetSearch('');
    // Load associated vet practices
    try {
      const assocRes = await api.get(`/crm-entities/${entity.id}/associations`);
      const assocs = assocRes.data || [];
      const practiceIds = assocs.map(a => a.crm_entity_1_id === entity.id ? a.crm_entity_2_id : a.crm_entity_1_id);
      const practices = allContacts.filter(c => practiceIds.includes(c.id) && c.organization_industry_role === 'veterinary_care');
      setVetAssocPractices(practices);
      if (practices.length === 1) {
        setForm(f => ({ ...f, vet_practice_crm_entity_id: practices[0].id, vet_practice: practices[0].system_name }));
      }
    } catch { setVetAssocPractices([]); }
  };

  const handleQuickCreate = async () => {
    if (quickCreate === 'vet') {
      if (!newName || !newLast) { toast.error('First and last name required'); return; }
      setSaving(true);
      try {
        const res = await api.post('/crm-entities', { crm_type: 'person', crm_class: 'contact', person_first_name: newName, person_last_name: newLast, person_industry_role: 'veterinarian', system_name: `${newName} ${newLast}` });
        setForm(f => ({ ...f, vet_crm_entity_id: res.data.id, vet_name: res.data.system_name }));
        setAllContacts(prev => [...prev, res.data]);
        toast.success('Veterinarian created');
      } catch { toast.error('Failed to create'); }
      setSaving(false);
    } else if (quickCreate === 'practice') {
      if (!newName) { toast.error('Practice name required'); return; }
      setSaving(true);
      try {
        const res = await api.post('/crm-entities', { crm_type: 'organization', crm_class: 'contact', organization_name: newName, organization_industry_role: 'veterinary_care', system_name: newName });
        setForm(f => ({ ...f, vet_practice_crm_entity_id: res.data.id, vet_practice: res.data.system_name }));
        setAllContacts(prev => [...prev, res.data]);
        toast.success('Practice created');
      } catch { toast.error('Failed to create'); }
      setSaving(false);
    } else if (quickCreate === 'client') {
      const isOrg = newType === 'organization';
      if (isOrg && !newOrgName) { toast.error('Organization name required'); return; }
      if (!isOrg && (!newName || !newLast)) { toast.error('First and last name required'); return; }
      setSaving(true);
      try {
        const displayName = isOrg ? newOrgName.trim() : `${newName} ${newLast}`.trim();
        const payload = isOrg
          ? { crm_type: 'organization', crm_class: 'contact', organization_name: newOrgName, system_name: newOrgName }
          : { crm_type: 'person', crm_class: 'contact', person_first_name: newName, person_last_name: newLast, system_name: displayName };
        const res = await api.post('/crm-entities', payload);
        setForm(f => ({ ...f, client_crm_entity_id: res.data.id, client_name: res.data.system_name }));
        setAllContacts(prev => [...prev, res.data]);
        toast.success('Client created');
      } catch { toast.error('Failed to create'); }
      setSaving(false);
    }
    setQuickCreate(null); setNewName(''); setNewLast('');
  };

  const handleCreate = async () => {
    const procedure_date_time = (form.date_part && form.time_part) ? `${form.date_part}T${form.time_part}:00` : '';
    if (!form.sale_opportunity_id || !form.vet_name || !form.vet_practice || !(form.procedures || []).length || !procedure_date_time) {
      toast.error('Horse, Vet Name, Practice, Procedure, and Date & Time are all required');
      return;
    }
    setSaving(true);
    try {
      await api.post('/vetting-procedures', { sale_id: saleId, ...form, procedure_date_time, procedure: (form.procedures || []).join(', ') });
      toast.success('Procedure created');
      onCreated();
    } catch { toast.error('Failed to create'); }
    setSaving(false);
  };

  const [focusedField, setFocusedField] = useState(null);

  // CrmSearchField is defined at module level — do not redefine here

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>New Vetting Procedure</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Horse *</Label>
            <Select value={form.sale_opportunity_id} onValueChange={(v) => setForm({ ...form, sale_opportunity_id: v })}>
              <SelectTrigger><SelectValue placeholder="Select horse..." /></SelectTrigger>
              <SelectContent>
                {opportunities.map(o => (
                  <SelectItem key={o.id} value={o.id}>#{o.hip_number || 'TBD'} - {o.opportunity_name?.split(' - ')[1] || o.opportunity_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <CrmSearchField label="Vet Name" value={form.vet_name} search={vetSearch} onSearch={setVetSearch}
              results={filteredVets} onSelect={selectVet} onClear={() => { setForm(f => ({ ...f, vet_crm_entity_id: null, vet_name: '', vet_practice_crm_entity_id: null, vet_practice: '' })); setVetAssocPractices([]); }}
              createType="vet" placeholder="Search vets..." fieldKey="vet"
              focusedField={focusedField} setFocusedField={setFocusedField}
              quickCreate={quickCreate} setQuickCreate={setQuickCreate}
              newName={newName} setNewName={setNewName} newLast={newLast} setNewLast={setNewLast}
              onQuickCreate={handleQuickCreate} saving={saving} />
            <CrmSearchField label="Vet Practice" value={form.vet_practice} search={practiceSearch} onSearch={setPracticeSearch}
              results={filteredPractices} onSelect={(e) => { setForm(f => ({ ...f, vet_practice_crm_entity_id: e.id, vet_practice: e.system_name })); setPracticeSearch(''); }}
              onClear={() => setForm(f => ({ ...f, vet_practice_crm_entity_id: null, vet_practice: '' }))}
              createType="practice" placeholder="Search practices..." fieldKey="practice"
              focusedField={focusedField} setFocusedField={setFocusedField}
              quickCreate={quickCreate} setQuickCreate={setQuickCreate}
              newName={newName} setNewName={setNewName} newLast={newLast} setNewLast={setNewLast}
              onQuickCreate={handleQuickCreate} saving={saving} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Client — dedicated Person/Org quick-create matching card form pattern */}
            <div className="space-y-2">
              <Label>Client</Label>
              {form.client_name ? (
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">{form.client_name}</Badge>
                  <Button variant="ghost" size="sm" className="h-6 px-1 text-xs" onClick={() => setForm(f => ({ ...f, client_crm_entity_id: null, client_name: '' }))}>Change</Button>
                </div>
              ) : quickCreate === 'client' ? (
                <div className="space-y-2 p-2 border rounded">
                  <div className="flex gap-1">
                    <Button size="sm" type="button" variant={newType === 'person' ? 'default' : 'outline'} className="h-6 text-xs flex-1" onClick={() => setNewType('person')}>Person</Button>
                    <Button size="sm" type="button" variant={newType === 'organization' ? 'default' : 'outline'} className="h-6 text-xs flex-1" onClick={() => setNewType('organization')}>Organization</Button>
                  </div>
                  {newType === 'person' ? (
                    <div className="grid grid-cols-2 gap-2">
                      <Input placeholder="First Name *" value={newName} onChange={e => setNewName(e.target.value)} className="text-sm" />
                      <Input placeholder="Last Name *" value={newLast} onChange={e => setNewLast(e.target.value)} className="text-sm" />
                    </div>
                  ) : (
                    <Input placeholder="Organization Name *" value={newOrgName} onChange={e => setNewOrgName(e.target.value)} className="text-sm" />
                  )}
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleQuickCreate} disabled={saving} className="bg-primary hover:bg-primary/90 text-white text-xs">Create</Button>
                    <Button size="sm" variant="outline" onClick={() => setQuickCreate(null)} className="text-xs">Cancel</Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                    <Input className="pl-8 h-8 text-sm" placeholder="Search clients..." value={clientSearch} onChange={e => setClientSearch(e.target.value)}
                      onFocus={() => setFocusedField('client')} onBlur={() => setTimeout(() => setFocusedField(null), 200)} />
                  </div>
                  {focusedField === 'client' && filteredClients.slice(0, 6).length > 0 && (
                    <div className="border rounded max-h-24 overflow-y-auto">
                      {filteredClients.slice(0, 6).map(e => (
                        <div key={e.id} className="p-1.5 text-xs hover:bg-muted/50 cursor-pointer" onClick={async () => { setForm(f => ({ ...f, client_crm_entity_id: e.id, client_name: e.system_name })); setClientSearch(''); }}>
                          {e.system_name}
                        </div>
                      ))}
                    </div>
                  )}
                  <Button variant="link" size="sm" className="h-auto p-0 text-xs"
                    onClick={async () => { setQuickCreate('client'); setNewName(''); setNewLast(''); setNewOrgName(''); setNewType('person'); }}>
                    + Create New
                  </Button>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Procedure *</Label>
              <div className="border rounded p-2 max-h-36 overflow-y-auto space-y-1">
                {[
                  { value: 'heart_scan', label: 'Heart Scan' },
                  { value: 'measurement', label: 'Measurement' },
                  { value: 'physical', label: 'Physical' },
                  { value: 'scope_in_person', label: 'Scope - In Person' },
                  { value: 'scope_repository', label: 'Scope - Repository' },
                  { value: 'ultrasound', label: 'Ultrasound' },
                  { value: 'xray_in_person', label: 'X-ray - In Person' },
                  { value: 'xray_repository', label: 'X-ray - Repository' },
                  { value: 'other', label: 'Other' },
                ].map(opt => (
                  <label key={opt.value} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/30 p-1 rounded">
                    <input type="checkbox" className="w-3.5 h-3.5" checked={(form.procedures || []).includes(opt.value)}
                      onChange={async (e) => {
                        const procs = form.procedures || [];
                        setForm({ ...form, procedures: e.target.checked ? [...procs, opt.value] : procs.filter(p => p !== opt.value) });
                      }} />
                    <span className="text-xs">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {(form.procedures || []).includes('other') && (
            <div className="space-y-2">
              <Label>Other Procedure Name *</Label>
              <Input value={form.other_procedure_name || ''} onChange={(e) => setForm({ ...form, other_procedure_name: e.target.value })} placeholder="Enter procedure name..." />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Date / Time *</Label>
              <div className="flex gap-2 flex-wrap">
                <Input
                  type="date"
                  value={form.date_part || ''}
                  onChange={(e) => setForm({ ...form, date_part: e.target.value })}
                  className={`w-36 h-9 text-sm ${form.date_part ? 'date-filled' : ''}`}
                />
                <TimeSelect value={form.time_part || ''} onChange={(v) => setForm({ ...form, time_part: v })} />
              </div>
            </div>
            <div className="flex items-end gap-2 pb-1">
              <Checkbox id="vet-report" checked={!!form.viewed_vet_report} onCheckedChange={(v) => setForm({ ...form, viewed_vet_report: v })} />
              <Label htmlFor="vet-report">Viewed Vet Report</Label>
            </div>
          </div>

          {(form.procedures || []).some(p => p === 'scope_repository') && (
            <div className="space-y-2">
              <Label>Time Spent Watching (seconds)</Label>
              <Input type="number" min="0" placeholder="0" value={form.time_spent_watching || ''} onChange={(e) => setForm({ ...form, time_spent_watching: parseInt(e.target.value) || null })} />
            </div>
          )}
          <div className="space-y-2">
            <Label>Notes</Label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Any notes..." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleCreate} disabled={saving} className="bg-primary hover:bg-primary/90 text-white">{saving ? 'Creating...' : 'Create Procedure'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* --- Edit Procedure Dialog --- */
const EditProcedureDialog = ({ procedure, onClose, onSaved }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [allContacts, setAllContacts] = useState([]);
  const [vetSearch, setVetSearch] = useState('');
  const [practiceSearch, setPracticeSearch] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [vetAssocPractices, setVetAssocPractices] = useState([]);
  const [quickCreate, setQuickCreate] = useState(null);
  const [newName, setNewName] = useState('');
  const [newLast, setNewLast] = useState('');
  const [newType, setNewType] = useState('person');
  const [newOrgName, setNewOrgName] = useState('');
  const [focusedField, setFocusedField] = useState(null);
  const [quickSaving, setQuickSaving] = useState(false);

  const parseParts = (dt) => {
    if (!dt) return { date_part: '', time_part: '' };
    const d = new Date(dt);
    if (isNaN(d)) return { date_part: '', time_part: '' };
    return { date_part: d.toISOString().slice(0, 10), time_part: `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}` };
  };

  useEffect(() => {
    if (procedure) {
      setForm({
        vet_crm_entity_id: procedure.vet_crm_entity_id || null,
        vet_name: procedure.vet_name || '',
        vet_practice_crm_entity_id: procedure.vet_practice_crm_entity_id || null,
        vet_practice: procedure.vet_practice || '',
        client_crm_entity_id: procedure.client_crm_entity_id || null,
        client_name: procedure.client_name || '',
        procedure: procedure.procedure || '',
        ...parseParts(procedure.procedure_date_time),
        viewed_vet_report: procedure.viewed_vet_report || false,
        notes: procedure.notes || '',
        time_spent_watching: procedure.time_spent_watching || '',
      });
      setVetSearch(''); setPracticeSearch(''); setClientSearch('');
      setVetAssocPractices([]); setQuickCreate(null);
      api.get('/crm-entities').then(r => setAllContacts(r.data || [])).catch(() => {});
    }
  }, [procedure]);

  const vets = allContacts.filter(c => c.person_industry_role === 'veterinarian' || c.person_industry_role === 'vet');
  const vetPractices = allContacts.filter(c => c.organization_industry_role === 'veterinary_care');
  const filteredVets = vetSearch.length > 0 ? vets.filter(v => v.system_name?.toLowerCase().includes(vetSearch.toLowerCase())) : vets;
  const filteredPractices = vetAssocPractices.length > 0
    ? vetAssocPractices
    : (practiceSearch.length > 0 ? vetPractices.filter(v => v.system_name?.toLowerCase().includes(practiceSearch.toLowerCase())) : vetPractices);
  const filteredClients = clientSearch.length > 0
    ? allContacts.filter(c => c.system_name?.toLowerCase().includes(clientSearch.toLowerCase())).slice(0, 10)
    : [];

  const selectVet = async (entity) => {
    setForm(f => ({ ...f, vet_crm_entity_id: entity.id, vet_name: entity.system_name }));
    setVetSearch('');
    try {
      const assocRes = await api.get(`/crm-entities/${entity.id}/associations`);
      const assocs = assocRes.data || [];
      const practiceIds = assocs.map(a => a.crm_entity_1_id === entity.id ? a.crm_entity_2_id : a.crm_entity_1_id);
      const practices = allContacts.filter(c => practiceIds.includes(c.id) && c.organization_industry_role === 'veterinary_care');
      setVetAssocPractices(practices);
      if (practices.length === 1) {
        setForm(f => ({ ...f, vet_practice_crm_entity_id: practices[0].id, vet_practice: practices[0].system_name }));
      }
    } catch { setVetAssocPractices([]); }
  };

  const handleQuickCreate = async () => {
    setQuickSaving(true);
    try {
      if (quickCreate === 'vet') {
        if (!newName || !newLast) { toast.error('First and last name required'); setQuickSaving(false); return; }
        const res = await api.post('/crm-entities', { crm_type: 'person', crm_class: 'contact', person_first_name: newName, person_last_name: newLast, person_industry_role: 'veterinarian', system_name: `${newName} ${newLast}` });
        setForm(f => ({ ...f, vet_crm_entity_id: res.data.id, vet_name: res.data.system_name }));
        setAllContacts(prev => [...prev, res.data]);
        toast.success('Veterinarian created');
      } else if (quickCreate === 'practice') {
        if (!newName) { toast.error('Practice name required'); setQuickSaving(false); return; }
        const res = await api.post('/crm-entities', { crm_type: 'organization', crm_class: 'contact', organization_name: newName, organization_industry_role: 'veterinary_care', system_name: newName });
        setForm(f => ({ ...f, vet_practice_crm_entity_id: res.data.id, vet_practice: res.data.system_name }));
        setAllContacts(prev => [...prev, res.data]);
        toast.success('Practice created');
      } else if (quickCreate === 'client') {
        const isOrg = newType === 'organization';
        const displayName = isOrg ? newOrgName.trim() : `${newName} ${newLast}`.trim();
        if (isOrg && !newOrgName) { toast.error('Organization name required'); setQuickSaving(false); return; }
        if (!isOrg && (!newName || !newLast)) { toast.error('First and last name required'); setQuickSaving(false); return; }
        const payload = isOrg
          ? { crm_type: 'organization', crm_class: 'contact', organization_name: newOrgName, system_name: newOrgName }
          : { crm_type: 'person', crm_class: 'contact', person_first_name: newName, person_last_name: newLast, system_name: displayName };
        const res = await api.post('/crm-entities', payload);
        setForm(f => ({ ...f, client_crm_entity_id: res.data.id, client_name: res.data.system_name }));
        setAllContacts(prev => [...prev, res.data]);
        toast.success('Client created');
      }
    } catch { toast.error('Failed to create'); }
    setQuickCreate(null); setNewName(''); setNewLast('');
    setQuickSaving(false);
  };

  // CrmSearchField is defined at module level — do not redefine here

  const handleSave = async () => {
    const procedure_date_time = (form.date_part && form.time_part) ? `${form.date_part}T${form.time_part}:00` : null;
    setSaving(true);
    try {
      await api.put(`/vetting-procedures/${procedure.id}`, { ...form, procedure_date_time });
      toast.success('Procedure updated');
      onSaved();
    } catch { toast.error('Failed to update'); }
    setSaving(false);
  };

  return (
    <Dialog open={!!procedure} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Edit Vetting Procedure</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-4">
            <CrmSearchField
              label="Vet Name" value={form.vet_name} search={vetSearch} onSearch={setVetSearch}
              results={filteredVets} onSelect={selectVet}
              onClear={() => { setForm(f => ({ ...f, vet_crm_entity_id: null, vet_name: '', vet_practice_crm_entity_id: null, vet_practice: '' })); setVetAssocPractices([]); }}
              createType="vet" placeholder="Search vets..." fieldKey="vet"
              focusedField={focusedField} setFocusedField={setFocusedField}
              quickCreate={quickCreate} setQuickCreate={setQuickCreate}
              newName={newName} setNewName={setNewName} newLast={newLast} setNewLast={setNewLast}
              onQuickCreate={handleQuickCreate} saving={quickSaving}
            />
            <CrmSearchField
              label="Vet Practice" value={form.vet_practice} search={practiceSearch} onSearch={setPracticeSearch}
              results={filteredPractices} onSelect={(e) => { setForm(f => ({ ...f, vet_practice_crm_entity_id: e.id, vet_practice: e.system_name })); setPracticeSearch(''); }}
              onClear={() => setForm(f => ({ ...f, vet_practice_crm_entity_id: null, vet_practice: '' }))}
              createType="practice" placeholder="Search practices..." fieldKey="practice"
              focusedField={focusedField} setFocusedField={setFocusedField}
              quickCreate={quickCreate} setQuickCreate={setQuickCreate}
              newName={newName} setNewName={setNewName} newLast={newLast} setNewLast={setNewLast}
              onQuickCreate={handleQuickCreate} saving={quickSaving}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            {/* Client — card-form-style Person/Org quick-create */}
            <div className="space-y-2">
              <Label>Client</Label>
              {form.client_name ? (
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">{form.client_name}</Badge>
                  <Button variant="ghost" size="sm" className="h-6 px-1 text-xs" onClick={() => setForm(f => ({ ...f, client_crm_entity_id: null, client_name: '' }))}>Change</Button>
                </div>
              ) : quickCreate === 'client' ? (
                <div className="space-y-2 p-2 border rounded">
                  <div className="flex gap-1">
                    <Button size="sm" type="button" variant={newType === 'person' ? 'default' : 'outline'} className="h-6 text-xs flex-1" onClick={() => setNewType('person')}>Person</Button>
                    <Button size="sm" type="button" variant={newType === 'organization' ? 'default' : 'outline'} className="h-6 text-xs flex-1" onClick={() => setNewType('organization')}>Organization</Button>
                  </div>
                  {newType === 'person' ? (
                    <div className="grid grid-cols-2 gap-2">
                      <Input placeholder="First Name *" value={newName} onChange={e => setNewName(e.target.value)} className="text-sm" />
                      <Input placeholder="Last Name *" value={newLast} onChange={e => setNewLast(e.target.value)} className="text-sm" />
                    </div>
                  ) : (
                    <Input placeholder="Organization Name *" value={newOrgName} onChange={e => setNewOrgName(e.target.value)} className="text-sm" />
                  )}
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleQuickCreate} disabled={quickSaving} className="bg-primary hover:bg-primary/90 text-white text-xs">Create</Button>
                    <Button size="sm" variant="outline" onClick={() => setQuickCreate(null)} className="text-xs">Cancel</Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                    <Input className="pl-8 h-8 text-sm" placeholder="Search clients..." value={clientSearch} onChange={e => setClientSearch(e.target.value)}
                      onFocus={() => setFocusedField('client')} onBlur={() => setTimeout(() => setFocusedField(null), 200)} />
                  </div>
                  {focusedField === 'client' && filteredClients.slice(0, 6).length > 0 && (
                    <div className="border rounded max-h-24 overflow-y-auto">
                      {filteredClients.slice(0, 6).map(e => (
                        <div key={e.id} className="p-1.5 text-xs hover:bg-muted/50 cursor-pointer" onClick={async () => { setForm(f => ({ ...f, client_crm_entity_id: e.id, client_name: e.system_name })); setClientSearch(''); }}>
                          {e.system_name}
                        </div>
                      ))}
                    </div>
                  )}
                  <Button variant="link" size="sm" className="h-auto p-0 text-xs"
                    onClick={async () => { setQuickCreate('client'); setNewName(''); setNewLast(''); setNewOrgName(''); setNewType('person'); }}>
                    + Create New
                  </Button>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Procedure</Label>
              <Select value={form.procedure || ''} onValueChange={(v) => setForm({ ...form, procedure: v })}>
                <SelectTrigger><SelectValue placeholder="Select type..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="heart_scan">Heart Scan</SelectItem>
                  <SelectItem value="measurement">Measurement</SelectItem>
                  <SelectItem value="physical">Physical</SelectItem>
                  <SelectItem value="scope_in_person">Scope - In Person</SelectItem>
                  <SelectItem value="scope_repository">Scope - Repository</SelectItem>
                  <SelectItem value="ultrasound">Ultrasound</SelectItem>
                  <SelectItem value="xray_in_person">X-ray - In Person</SelectItem>
                  <SelectItem value="xray_repository">X-ray - Repository</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Date / Time</Label>
            <div className="flex gap-2 flex-wrap">
              <Input type="date" value={form.date_part || ''} onChange={(e) => setForm({ ...form, date_part: e.target.value })} className={`w-36 h-9 text-sm ${form.date_part ? 'date-filled' : ''}`} />
              <TimeSelect value={form.time_part || ''} onChange={(v) => setForm({ ...form, time_part: v })} />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox id="edit-vet-report" checked={!!form.viewed_vet_report} onCheckedChange={(v) => setForm({ ...form, viewed_vet_report: v })} />
            <Label htmlFor="edit-vet-report">Viewed Vet Report</Label>
          </div>
          {(form.procedure || '').startsWith('scope') && (
            <div className="space-y-2">
              <Label>Time Spent Watching (seconds)</Label>
              <Input type="number" min="0" value={form.time_spent_watching || ''} onChange={(e) => setForm({ ...form, time_spent_watching: parseInt(e.target.value) || null })} />
            </div>
          )}
          <div className="space-y-2">
            <Label>Notes</Label>
            <Input value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90 text-white">{saving ? 'Saving...' : 'Save Changes'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* --- OCR Procedure Dialog --- */
const OcrProcedureDialog = ({ open, onOpenChange, saleId, onCreated }) => {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const stopCamera = () => { if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; } setCameraActive(false); };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      streamRef.current = stream;
      setCameraActive(true);
      setTimeout(() => { if (videoRef.current) videoRef.current.srcObject = stream; }, 100);
    } catch { toast.error('Unable to access camera.'); }
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvas.getContext('2d').drawImage(videoRef.current, 0, 0);
    canvas.toBlob(async (blob) => { stopCamera(); if (blob) await processFile(new File([blob], 'capture.jpg', { type: 'image/jpeg' })); }, 'image/jpeg', 0.9);
  };

  const processFile = async (file) => {
    setUploading(true); setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/vetting-procedures/ocr-upload?sale_id=${saleId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(res.data);
      toast.success('Procedure scanned');
    } catch { toast.error('OCR failed.'); }
    setUploading(false);
  };

  const handleCreateFromOCR = async () => {
    if (!result) return;
    setUploading(true);
    try {
      await api.post('/vetting-procedures', { sale_id: saleId, vet_name: result.vet_name || '', vet_practice: result.vet_practice || '', client_name: result.client_name || '', procedure: result.procedure || '', notes: result.notes || '', procedure_date_time: new Date().toISOString() });
      toast.success('Procedure created from scan');
      setResult(null);
      onCreated();
    } catch { toast.error('Failed to create'); }
    setUploading(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setResult(null); stopCamera(); } onOpenChange(v); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Scan Procedure Form</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-sm text-muted-foreground">Take a photo or upload an image of a vetting procedure form.</p>
          {cameraActive ? (
            <div className="space-y-2">
              <video ref={videoRef} autoPlay playsInline className="w-full rounded border" />
              <div className="flex gap-2">
                <Button className="flex-1 bg-primary hover:bg-primary/90 text-white" onClick={capturePhoto}>Capture</Button>
                <Button variant="outline" className="flex-1" onClick={stopCamera}>Cancel</Button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={startCamera} disabled={uploading}><Eye className="w-4 h-4 mr-2" /> Take Photo</Button>
              <Button variant="outline" className="flex-1" onClick={() => document.getElementById('vet-ocr-file').click()} disabled={uploading}><Upload className="w-4 h-4 mr-2" /> Upload Image</Button>
            </div>
          )}
          <input id="vet-ocr-file" type="file" accept="image/*" onChange={async (e) => { if (e.target.files?.[0]) processFile(e.target.files[0]); }} className="hidden" />
          {uploading && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Processing...</div>}
          {result && (
            <div className="space-y-2 p-3 border rounded bg-muted/30 text-sm">
              <p><strong>Vet:</strong> {result.vet_name || '-'}</p>
              <p><strong>Practice:</strong> {result.vet_practice || '-'}</p>
              <p><strong>Client:</strong> {result.client_name || '-'}</p>
              <p><strong>Procedure:</strong> {result.procedure || '-'}</p>
              {result.notes && <p><strong>Notes:</strong> {result.notes}</p>}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          {result && <Button onClick={handleCreateFromOCR} disabled={uploading} className="bg-primary hover:bg-primary/90 text-white">Create from Scan</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VettingProceduresPage;
